import React from 'react';

import Button from 'common/inputs/Button';
import Link from 'common/Link';
import { H2, H3, P, Span } from 'common/ui/Text';
import nbspLastSpace from 'common/util/nbspLastSpace';
import numberWithCommas from 'common/util/numberWithCommas';
import styles from 'css-module/components/www/_PricingV4.module.scss';

import PricingV4CardFeature from './PricingV4CardFeature';
import PricingV4Plans, { type Plan, getPricePoint } from './PricingV4Plans';

import type { Timeframe } from './Timeframe';

type Props = {
  timeframe: Timeframe;
  trackedUsers: number;
};
export default function PricingV4PlanCards({ timeframe, trackedUsers }: Props) {
  return (
    <div className={styles.pricingV4PlanCards}>
      <PricingV4PlanCard
        plan={PricingV4Plans.free}
        timeframe={timeframe}
        trackedUsers={trackedUsers}
      />
      <PricingV4PlanCard
        plan={PricingV4Plans.core}
        previousPlan={PricingV4Plans.free}
        timeframe={timeframe}
        trackedUsers={trackedUsers}
      />
      <PricingV4PlanCard
        plan={PricingV4Plans.pro}
        previousPlan={PricingV4Plans.core}
        timeframe={timeframe}
        trackedUsers={trackedUsers}
      />
      <PricingV4PlanCard
        plan={PricingV4Plans.business}
        previousPlan={PricingV4Plans.pro}
        timeframe={timeframe}
        trackedUsers={trackedUsers}
      />
    </div>
  );
}

function PricingV4PlanCardPrice({
  plan,
  timeframe,
  trackedUsers,
}: {
  plan: Plan;
  timeframe: Timeframe;
  trackedUsers: number;
}) {
  if (!plan.pricePoints) {
    return (
      <div className={styles.planCardPrice}>
        <Span className={styles.priceStartsAt} variant="bodySm">
          &nbsp;
        </Span>
        <H2 variant="heading3Xl">Let's talk</H2>
      </div>
    );
  }

  const pricePoint =
    getPricePoint(plan, trackedUsers) || plan.pricePoints[plan.pricePoints.length - 1];
  const timeframePrice = pricePoint[timeframe];
  const showStartsAt = pricePoint === plan.pricePoints[0] && timeframePrice > 0;
  return (
    <div className={styles.planCardPrice}>
      <Span className={styles.priceStartsAt} variant="bodySm">
        {showStartsAt ? 'starts at' : null}
        &nbsp;
      </Span>
      <H2 variant="heading3Xl">
        ${numberWithCommas(timeframePrice)}
        <Span variant="bodySm">&nbsp;/&nbsp;month</Span>
        {timeframe === 'yearly' && timeframePrice > 0 && (
          <Span variant="bodySm">&nbsp;billed&nbsp;yearly</Span>
        )}
      </H2>
    </div>
  );
}

type PlanCardProps = {
  plan: Plan;
  previousPlan?: Plan;
  timeframe: Timeframe;
  trackedUsers: number;
};
function PricingV4PlanCard({ plan, previousPlan, timeframe, trackedUsers }: PlanCardProps) {
  const pricePoint = getPricePoint(plan, trackedUsers);
  const disabled = plan.pricePoints && !pricePoint;
  const buttonType =
    typeof plan.cta.buttonType === 'function'
      ? plan.cta.buttonType(trackedUsers)
      : plan.cta.buttonType;
  return (
    <div className={styles.planCard} data-disabled={disabled} data-most-popular={plan.mostPopular}>
      <PricingV4PlanCardPrice plan={plan} timeframe={timeframe} trackedUsers={trackedUsers} />
      <div className={styles.planCardContent}>
        <H3 variant="headingLg">{plan.name}</H3>
        <P className={styles.planCardDescription} variant="bodyMd">
          {nbspLastSpace(plan.description)}
        </P>
      </div>
      <Link className={styles.planCardCTA} to={plan.cta.href}>
        <Button
          buttonType={buttonType}
          className={styles.planCardButton}
          tabIndex={-1}
          value={plan.cta.label}
        />
      </Link>
      <div className={styles.planCardFeatures}>
        <P variant="headingSm">
          {previousPlan ? `All ${previousPlan.name} features, plus:` : `What you get:`}
        </P>
        <ul>
          {plan.features.map((feature, i) => {
            return (
              <PricingV4CardFeature
                key={i}
                feature={typeof feature === 'function' ? feature(trackedUsers) : feature}
              />
            );
          })}
        </ul>
      </div>
      {plan.mostPopular && (
        <Span className={styles.planCardMostPopular} fontWeight="medium" variant="bodySm">
          Most popular
        </Span>
      )}
    </div>
  );
}
